import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Share2, Download, Link2, Loader2, MessageCircle } from "lucide-react";
import { shareCard, downloadCard, copyLink, openWhatsApp } from "@/lib/share";
import { toast } from "sonner";

// Reusable share control backed by a server-rendered PNG card.
// Pass `waText` to add a "Share via WhatsApp" action (17-C item 6) — the
// message is `${waText} ${shareUrl}` so pasted links unfurl via the share page.
export const ShareButton = ({
  imageUrl,
  shareUrl,
  title = "CivicHearth",
  text = "",
  waText = "",
  filename = "civichearth-card.png",
  label = "Share",
  variant = "outline",
  className = "",
  testId = "share-button",
}) => {
  const [busy, setBusy] = useState(false);
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  const doShare = async () => {
    setBusy(true);
    const r = await shareCard(imageUrl, { title, text, url: shareUrl, filename });
    setBusy(false);
    if (r === "unsupported") {
      await downloadCard(imageUrl, filename);
      toast.success("Card downloaded — share it anywhere");
    }
  };

  const doDownload = async () => {
    setBusy(true);
    try {
      await downloadCard(imageUrl, filename);
      toast.success("Card downloaded");
    } catch {
      toast.error("Could not download the card");
    }
    setBusy(false);
  };

  const doCopy = async () => {
    const ok = await copyLink(shareUrl);
    ok ? toast.success("Link copied") : toast.error("Could not copy link");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button data-testid={testId} variant={variant} className={`rounded-full ${className}`} disabled={busy}>
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {waText && (
          <DropdownMenuItem data-testid={`${testId}-whatsapp`} onClick={() => openWhatsApp(`${waText} ${shareUrl}`)}>
            <MessageCircle className="w-4 h-4 mr-2 text-green-600" /> Share via WhatsApp
          </DropdownMenuItem>
        )}
        {canNativeShare && (
          <DropdownMenuItem data-testid={`${testId}-native`} onClick={doShare}>
            <Share2 className="w-4 h-4 mr-2" /> Share…
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid={`${testId}-download`} onClick={doDownload}>
          <Download className="w-4 h-4 mr-2" /> Download image
        </DropdownMenuItem>
        <DropdownMenuItem data-testid={`${testId}-copy`} onClick={doCopy}>
          <Link2 className="w-4 h-4 mr-2" /> Copy link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
